<template>
  <!-- bootstrap-vue 2.23 b-dropdown'ın kullanılan alt kümesi, aynı BS4 DOM'u. Popper yok; menü
       Popper'ın yazdığı satır içi stille (top/left 0 + translate3d, x-placement) toggle'a hizalanır,
       böylece temanın .dropdown-menu kuralları (ör. .iq-card-header'da top: 20px !important) aynı
       sonucu verir. Sınıra (görünüm alanı / en yakın kaydırma kabı) sığmazsa ve üstte yer varsa
       yukarı açılır, yatayda kaydırılır. Aynı anda tek menü açık kalır. -->
  <div :id="baseId" class="dropdown b-dropdown btn-group" :class="{ show: visible }" @keydown="onKeydown">
    <button ref="toggle" type="button" :id="toggleId" aria-haspopup="menu" :aria-expanded="visible ? 'true' : 'false'" class="btn dropdown-toggle" :class="toggleClasses" @click="toggle"><slot name="button-content">{{ text }}</slot></button>
    <ul ref="menu" role="menu" tabindex="-1" :aria-labelledby="toggleId" class="dropdown-menu" :class="menuClasses" :style="menuStyle" :x-placement="placement"><slot :hide="hide" /></ul>
  </div>
</template>

<script>
  let uid = 0;
  let openDropdown = null;

  const ITEM_SELECTOR = '.dropdown-item:not(.disabled):not([disabled])';

  /* Popper'ın varsayılan sınırı gibi: görünüm alanı ile en yakın kaydırma kaplarının kesişimi. */
  function scrollBoundary(element) {
    const box = { top: 0, left: 0, bottom: window.innerHeight, right: document.documentElement.clientWidth };
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
      const style = window.getComputedStyle(node);
      if (/(auto|scroll|hidden)/.test(style.overflowY + style.overflowX)) {
        const rect = node.getBoundingClientRect();
        box.top = Math.max(box.top, rect.top);
        box.left = Math.max(box.left, rect.left);
        box.bottom = Math.min(box.bottom, rect.bottom);
        box.right = Math.min(box.right, rect.right);
      }
    }
    return box;
  }

  const BOUNDARY_PADDING = 5; // Popper preventOverflow varsayılanı

  export default {
    name: 'BaseDropdown',
    emits: ['show', 'shown', 'hide', 'hidden'],
    provide() {
      return { baseDropdown: this };
    },
    props: {
      id: { type: String, default: null },
      text: { type: String, default: '' },
      variant: { type: String, default: 'secondary' },
      size: { type: String, default: null },
      right: { type: Boolean, default: false },
      noCaret: { type: Boolean, default: false },
      toggleClass: { type: [String, Array, Object], default: null },
      menuClass: { type: [String, Array, Object], default: null }
    },
    data() {
      uid += 1;
      return {
        uid,
        visible: false,
        placement: null,
        offset: null
      };
    },
    computed: {
      baseId() {
        return this.id || `base-dropdown-${this.uid}`;
      },
      toggleId() {
        return `${this.baseId}__BV_toggle_`;
      },
      toggleClasses() {
        return [
          this.variant ? `btn-${this.variant}` : '',
          this.size ? `btn-${this.size}` : '',
          this.toggleClass,
          { 'dropdown-toggle-no-caret': this.noCaret }
        ];
      },
      menuClasses() {
        return [this.menuClass, { 'dropdown-menu-end': this.right, show: this.visible }];
      },
      menuStyle() {
        if (!this.visible || !this.offset) return null;
        return {
          position: 'absolute',
          transform: `translate3d(${this.offset.x}px, ${this.offset.y}px, 0px)`,
          top: '0px',
          left: '0px',
          willChange: 'transform'
        };
      }
    },
    beforeUnmount() {
      this.removeListeners();
      if (openDropdown === this) openDropdown = null;
    },
    methods: {
      toggle(event) {
        if (event) event.preventDefault();
        if (this.visible) this.hide(true);
        else this.show();
      },
      show() {
        if (this.visible) return;
        if (openDropdown && openDropdown !== this) openDropdown.hide(false);
        openDropdown = this;
        this.$emit('show');
        this.placement = null;
        this.offset = null;
        this.visible = true;
        this.addListeners();
        this.$nextTick(() => {
          this.updatePlacement();
          this.$emit('shown');
        });
      },
      hide(refocus = false) {
        if (!this.visible) return;
        this.$emit('hide');
        this.visible = false;
        this.placement = null;
        this.offset = null;
        this.removeListeners();
        if (openDropdown === this) openDropdown = null;
        if (refocus && this.$refs.toggle) this.$refs.toggle.focus();
        this.$nextTick(() => this.$emit('hidden'));
      },
      /* Popper 1 (bootstrap-vue varsayılanları): bottom-start / bottom-end, aşağı sığmazsa ve üstte
         yer varsa top-* (flip); yatayda sınır içine 5px payla kaydırılır (preventOverflow). */
      updatePlacement() {
        const menu = this.$refs.menu;
        const toggle = this.$refs.toggle;
        if (!menu || !toggle || !this.visible) return;

        // Taban konum: Popper'ın satır içi stili, dönüşüm yok (tema kuralları bunun üstüne uygulanır).
        menu.style.position = 'absolute';
        menu.style.top = '0px';
        menu.style.left = '0px';
        menu.style.transform = 'none';
        const base = menu.getBoundingClientRect();
        const menuStyle = window.getComputedStyle(menu);
        const outerHeight = base.height + (parseFloat(menuStyle.marginTop) || 0) + (parseFloat(menuStyle.marginBottom) || 0);
        const wrap = this.$el.getBoundingClientRect();
        const toggleRect = toggle.getBoundingClientRect();
        const boundary = scrollBoundary(this.$el);

        // Kararlar Popper'ın kendi hesabıyla (tema top/margin'i hariç) verilir; tema kuralları
        // sonuçta dönüşümün üstüne eklenir (Popper'da olduğu gibi).
        let x = (this.right ? toggleRect.right - base.width : toggleRect.left) - wrap.left;
        let y = toggleRect.bottom - wrap.top;
        let vertical = 'bottom';
        const spaceBelow = boundary.bottom - toggleRect.bottom;
        const spaceAbove = toggleRect.top - boundary.top;
        if (wrap.top + y + outerHeight > boundary.bottom && spaceAbove > spaceBelow && spaceAbove >= outerHeight) {
          vertical = 'top';
          y = toggleRect.top - wrap.top - outerHeight;
        }

        const minLeft = boundary.left + BOUNDARY_PADDING;
        const maxLeft = boundary.right - BOUNDARY_PADDING - base.width;
        x = Math.min(Math.max(wrap.left + x, minLeft), maxLeft) - wrap.left;

        this.placement = `${vertical}-${this.right ? 'end' : 'start'}`;
        this.offset = { x: Math.round(x), y: Math.round(y) };
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.transform = '';
      },
      addListeners() {
        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('focusin', this.onDocumentFocusin);
      },
      removeListeners() {
        document.removeEventListener('click', this.onDocumentClick, true);
        document.removeEventListener('focusin', this.onDocumentFocusin);
      },
      onDocumentClick(event) {
        if (!this.$el.contains(event.target)) this.hide(false);
      },
      onDocumentFocusin(event) {
        if (!this.$el.contains(event.target)) this.hide(false);
      },
      items() {
        return this.$refs.menu ? Array.from(this.$refs.menu.querySelectorAll(ITEM_SELECTOR)) : [];
      },
      /* Klavye: Esc kapatır ve odağı toggle'a verir; aşağı/yukarı menü öğeleri arasında gezer. */
      onKeydown(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          if (this.visible) {
            event.stopPropagation();
            this.hide(true);
          }
          return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        if (!this.visible) {
          this.show();
          this.$nextTick(() => { const first = this.items()[0]; if (first) first.focus(); });
          return;
        }
        const items = this.items();
        if (!items.length) return;
        const index = items.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown'
          ? Math.min(items.length - 1, index + 1)
          : Math.max(0, index - 1);
        items[next].focus();
      }
    }
  };
</script>

<style>
  /* bootstrap-vue kuralları (Aşama 5'e kadar bootstrap-vue-parity.css'teydi): `no-caret` ve menü odağı. */
  .dropdown:not(.dropleft) .dropdown-toggle.dropdown-toggle-no-caret::after {
    display: none !important;
  }
  .dropdown .dropdown-menu:focus {
    outline: none;
  }
</style>
